// ============================================================================
// DATE UTILITIES — TIMEZONE-SAFE DATE-ONLY FORMATTING
// ============================================================================
//
// Pure DATE columns (date_of_joining, date_of_birth, holiday_date,
// week_start_date, voucher_date, valid_from / valid_to, etc.) carry NO
// time-of-day meaning. Constructing a JavaScript `Date` from them and then
// formatting via `toLocaleDateString` or `toISOString` introduces the
// caller's local timezone into a value that should be timezone-agnostic.
// In timezones east or west of UTC this can cross day boundaries and the
// displayed date drifts by one day.
//
// These helpers operate purely on strings — they never construct a `Date`
// for date-only inputs — so the output is identical regardless of the
// caller's timezone or the API's serialization format.
//
// Use `formatDateOnly` for any DATE column. For TIMESTAMPTZ columns
// (created_at, updated_at, submitted_at, terminated_at, …) keep using a
// `new Date(iso).toLocaleString(...)` style — those carry an instant and
// are meant to display in the caller's local timezone.

const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'] as const;
const MONTHS_LONG  = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
] as const;
const WEEKDAYS_SHORT = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'] as const;
const WEEKDAYS_LONG  = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'] as const;

/**
 * Pull (year, month, day) from any of these inputs without instantiating
 * a `Date`:
 *   '2025-04-30'
 *   '2025-04-30T00:00:00'
 *   '2025-04-30T00:00:00.000Z'
 *   '2025-04-30T18:30:00.000+05:30'
 * Returns null when the input doesn't start with YYYY-MM-DD.
 */
function partsOf(s: string | null | undefined): { y: number; m: number; d: number } | null {
  if (!s) return null;
  const datePart = s.split('T')[0];
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(datePart);
  if (!m) return null;
  const y = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (!y || !month || !day) return null;
  return { y, m: month, d: day };
}

/**
 * Format a DATE column for display: "30 Apr 2025".
 * Returns the supplied placeholder for null / unparseable input.
 */
export function formatDateOnly(s: string | null | undefined, placeholder: string = '—'): string {
  const p = partsOf(s);
  if (!p) return placeholder;
  return `${String(p.d).padStart(2, '0')} ${MONTHS_SHORT[p.m - 1]} ${p.y}`;
}

/**
 * Long form: "Wednesday, 30 April 2025".
 */
export function formatDateOnlyLong(s: string | null | undefined, placeholder: string = '—'): string {
  const p = partsOf(s);
  if (!p) return placeholder;
  // Compute weekday locally — use UTC math so the answer is independent of
  // caller TZ. (Constructing as UTC midnight is safe here because we only
  // read .getUTCDay(), never display the constructed date.)
  const dow = new Date(Date.UTC(p.y, p.m - 1, p.d)).getUTCDay();
  return `${WEEKDAYS_LONG[dow]}, ${String(p.d).padStart(2, '0')} ${MONTHS_LONG[p.m - 1]} ${p.y}`;
}

/**
 * Returns the short weekday name ("Wed") for a DATE column, TZ-independent.
 */
export function weekdayShort(s: string | null | undefined, placeholder: string = '—'): string {
  const p = partsOf(s);
  if (!p) return placeholder;
  const dow = new Date(Date.UTC(p.y, p.m - 1, p.d)).getUTCDay();
  return WEEKDAYS_SHORT[dow];
}

/**
 * Returns the long weekday name ("Wednesday") for a DATE column, TZ-independent.
 */
export function weekdayLong(s: string | null | undefined, placeholder: string = '—'): string {
  const p = partsOf(s);
  if (!p) return placeholder;
  const dow = new Date(Date.UTC(p.y, p.m - 1, p.d)).getUTCDay();
  return WEEKDAYS_LONG[dow];
}

/**
 * A DATE column as the value of an `<input type="date">`: "2025-04-30".
 * Reads the date part of the string — never `new Date(s).toISOString()`,
 * which in IST turns "2025-04-30T00:00:00" into the previous day.
 */
export function toDateInputValue(s: string | null | undefined): string {
  const p = partsOf(s);
  if (!p) return '';
  return `${p.y}-${String(p.m).padStart(2, '0')}-${String(p.d).padStart(2, '0')}`;
}

/** Today's date in the user's own timezone, as YYYY-MM-DD (not the UTC date). */
export function todayIsoLocal(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

/**
 * Add `days` to a YYYY-MM-DD date string, returning YYYY-MM-DD.
 * Safe for any timezone — uses UTC math purely to advance the day count.
 */
export function addDaysIso(s: string, days: number): string {
  const p = partsOf(s);
  if (!p) return s;
  const t = Date.UTC(p.y, p.m - 1, p.d) + days * 86_400_000;
  const dt = new Date(t);
  const y = dt.getUTCFullYear();
  const m = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const d = String(dt.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
